import {
  useLayoutEffect,
  useRef,
  type DOMAttributes,
  type RefObject,
} from 'react';

/**
 * Preserves the user's place when a responsive switch removes focused tag UI.
 * @param isMobile - Whether the footer currently uses horizontal scrolling.
 * @param scrollRef - The same card's mobile scroll container.
 * @param groupRef - The stable tag group used when returning to desktop.
 * @returns Capture handlers covering the footer and its React-portal content.
 */
export const useCardFooterResponsiveFocus = (
  isMobile: boolean,
  scrollRef: RefObject<HTMLDivElement>,
  groupRef: RefObject<HTMLDivElement>
): Pick<DOMAttributes<HTMLDivElement>, 'onFocusCapture' | 'onBlurCapture'> => {
  const focusedElement = useRef<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (
      focusedElement.current &&
      ((!focusedElement.current.isConnected &&
        document.activeElement === document.body) ||
        (isMobile && document.activeElement === groupRef.current))
    ) {
      const target = isMobile ? scrollRef.current : groupRef.current;
      focusedElement.current = null;
      target?.focus({ preventScroll: true });
    }
  }, [isMobile, scrollRef, groupRef]);

  return {
    onFocusCapture: (event) => {
      focusedElement.current = event.target;
    },
    onBlurCapture: () => {
      focusedElement.current = null;
    },
  };
};
